import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, MessageSquare, Loader2, Paperclip, Send, Clock, CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CATEGORY_LABELS = {
  technical: "Problemi Tecnici",
  billing: "Fatturazione",
  subscription: "Abbonamento",
  suggestion: "Suggerimenti",
  other: "Altro"
};

const PRIORITY_LABELS = {
  low: "Bassa",
  medium: "Media",
  high: "Alta",
  urgent: "Urgente"
};

const STATUS_LABELS = {
  open: "Aperto",
  in_progress: "In Lavorazione",
  resolved: "Risolto",
  closed: "Chiuso"
};

const STATUS_COLORS = {
  open: "bg-blue-500",
  in_progress: "bg-yellow-500",
  resolved: "bg-green-500",
  closed: "bg-gray-500"
};

const PRIORITY_COLORS = {
  low: "bg-gray-400",
  medium: "bg-blue-400",
  high: "bg-orange-500",
  urgent: "bg-red-500"
};

export default function CompanySupport() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { ticketId } = useParams();
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  
  const [newTicket, setNewTicket] = useState({
    subject: "",
    category: "other",
    priority: "medium",
    message: ""
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.ticket_id === ticketId);
      if (ticket) setSelectedTicket(ticket);
    }
  }, [ticketId, tickets]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error("Compila oggetto e messaggio");
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/tickets`, {
        ...newTicket,
        attachments: attachments.map(a => a.url)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Ticket creato con successo");
      setShowNewTicket(false);
      setNewTicket({ subject: "", category: "other", priority: "medium", message: "" });
      setAttachments([]);
      fetchTickets();
      setSelectedTicket(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/tickets/${selectedTicket.ticket_id}/messages`, {
        message: newMessage,
        attachments: attachments.map(a => a.url)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Messaggio inviato");
      setNewMessage("");
      setAttachments([]);
      
      // Refresh ticket
      const res = await axios.get(`${API}/tickets/${selectedTicket.ticket_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTicket(res.data);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande (max 10MB)");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const res = await axios.post(`${API}/tickets/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setAttachments([...attachments, res.data]);
      toast.success("File caricato");
    } catch (error) {
      toast.error("Errore nel caricamento file");
    } finally {
      setUploading(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm("Chiudere questo ticket?")) return;
    
    try {
      await axios.put(`${API}/tickets/${selectedTicket.ticket_id}/status`, 
        { status: "closed" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Ticket chiuso");
      fetchTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error("Errore nella chiusura");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "open": return <Clock className="h-4 w-4" />;
      case "in_progress": return <AlertCircle className="h-4 w-4" />;
      case "resolved": return <CheckCircle2 className="h-4 w-4" />;
      case "closed": return <XCircle className="h-4 w-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Supporto <span className="text-primary">Tecnico</span>
            </h1>
            <p className="text-sm text-muted-foreground">Contatta il nostro team di supporto</p>
          </div>
          <Button onClick={() => setShowNewTicket(true)} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Ticket
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket List */}
          <div className="lg:col-span-1 card-dashboard">
            <h2 className="font-bold mb-4">I Tuoi Ticket</h2>
            {tickets.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessun ticket</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.ticket_id}
                    onClick={() => setSelectedTicket(ticket)}
                    className={`p-3 border cursor-pointer transition-colors ${
                      selectedTicket?.ticket_id === ticket.ticket_id 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{ticket.subject}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(ticket.created_at), "dd/MM/yyyy HH:mm", { locale: it })}
                        </p>
                      </div>
                      <Badge className={`${STATUS_COLORS[ticket.status]} text-white text-xs`}>
                        {STATUS_LABELS[ticket.status]}
                      </Badge>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">{CATEGORY_LABELS[ticket.category]}</Badge>
                      <Badge className={`${PRIORITY_COLORS[ticket.priority]} text-white text-xs`}>
                        {PRIORITY_LABELS[ticket.priority]}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Ticket Detail */}
          <div className="lg:col-span-2 card-dashboard">
            {selectedTicket ? (
              <div className="flex flex-col h-full">
                {/* Ticket Header */}
                <div className="border-b border-border pb-4 mb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="font-bold text-lg">{selectedTicket.subject}</h2>
                      <p className="text-sm text-muted-foreground">
                        Creato il {format(new Date(selectedTicket.created_at), "dd MMMM yyyy 'alle' HH:mm", { locale: it })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${STATUS_COLORS[selectedTicket.status]} text-white`}>
                        {getStatusIcon(selectedTicket.status)}
                        <span className="ml-1">{STATUS_LABELS[selectedTicket.status]}</span>
                      </Badge>
                      {selectedTicket.status !== "closed" && (
                        <Button variant="outline" size="sm" onClick={handleCloseTicket}>
                          Chiudi Ticket
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-[400px]">
                  {selectedTicket.messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`p-4 ${msg.sender_role === "admin" ? "bg-primary/10 ml-8" : "bg-muted/50 mr-8"}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-sm">{msg.sender_name}</span>
                        <Badge variant="outline" className="text-xs">
                          {msg.sender_role === "admin" ? "Supporto" : "Tu"}
                        </Badge>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {format(new Date(msg.created_at), "dd/MM/yyyy HH:mm", { locale: it })}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      {msg.attachments?.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {msg.attachments.map((att, idx) => (
                            <a
                              key={idx}
                              href={att}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              <Paperclip className="h-3 w-3" />
                              Allegato {idx + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                {selectedTicket.status !== "closed" && (
                  <div className="border-t border-border pt-4">
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Scrivi una risposta..."
                          className="min-h-[80px]"
                        />
                        {attachments.length > 0 && (
                          <div className="flex gap-2 mt-2">
                            {attachments.map((att, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {att.filename}
                                <button onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))} className="ml-1">
                                  ×
                                </button>
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                          <Button variant="outline" size="icon" asChild disabled={uploading}>
                            <span>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}</span>
                          </Button>
                        </label>
                        <Button onClick={handleSendMessage} disabled={sending || !newMessage.trim()} className="btn-primary">
                          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Seleziona un ticket per visualizzarlo</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Ticket di Supporto</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="input-label">Oggetto *</label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Descrivi brevemente il problema"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="input-label">Categoria</label>
                <Select
                  value={newTicket.category}
                  onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="input-label">Priorità</label>
                <Select
                  value={newTicket.priority}
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="input-label">Messaggio *</label>
              <Textarea
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                placeholder="Descrivi il problema in dettaglio..."
                className="min-h-[150px]"
              />
            </div>

            <div>
              <label className="input-label">Allegati</label>
              <div className="flex gap-2 flex-wrap">
                {attachments.map((att, idx) => (
                  <Badge key={idx} variant="secondary">
                    {att.filename}
                    <button onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))} className="ml-1">×</button>
                  </Badge>
                ))}
                <label className="cursor-pointer">
                  <input type="file" className="hidden" onChange={handleFileUpload} disabled={uploading} />
                  <Button variant="outline" size="sm" asChild disabled={uploading}>
                    <span>
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Paperclip className="h-4 w-4 mr-2" />}
                      Aggiungi File
                    </span>
                  </Button>
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Max 10MB per file. Formati: immagini, PDF, Word</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTicket(false)}>Annulla</Button>
            <Button onClick={handleCreateTicket} disabled={sending} className="btn-primary">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Crea Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
